import React, { useState, useEffect } from 'react';
import { getOCELMetadata } from '../../../services/apiClient';

/**
 * ObjectTypeFilterControl - Multi-select control for object types
 *
 * Allows users to select one or more object types to filter by.
 * Fetches available object types from OCEL metadata.
 *
 * @param {Array} value - Current selected object types (array of strings)
 * @param {Function} onChange - Callback when selection changes
 * @param {string} activeFile - Current active file for fetching metadata
 * @param {boolean} disabled - Whether control is disabled
 */
const ObjectTypeFilterControl = ({ value = [], onChange, activeFile, disabled = false }) => {
  const [availableTypes, setAvailableTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch available object types from metadata
  useEffect(() => {
    const fetchObjectTypes = async () => {
      if (!activeFile) return;

      setLoading(true);
      try {
        const metadata = await getOCELMetadata(activeFile);
        console.log('📊 ObjectTypeFilterControl - Fetched metadata:', metadata);

        if (metadata?.object_types) {
          // Handle both array and object format
          let types;
          if (Array.isArray(metadata.object_types)) {
            types = metadata.object_types;
          } else if (typeof metadata.object_types === 'object') {
            types = Object.keys(metadata.object_types);
          }

          if (types && types.length > 0) {
            setAvailableTypes(types);
            console.log(`✅ ObjectTypeFilterControl - Loaded ${types.length} object types`);
          }
        }
      } catch (err) {
        console.error('❌ ObjectTypeFilterControl - Failed to fetch object types:', err);
        setAvailableTypes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchObjectTypes();
  }, [activeFile]);

  const handleToggle = (type) => {
    if (disabled) return;

    const newValue = value.includes(type)
      ? value.filter(t => t !== type)
      : [...value, type];

    onChange(newValue);
  };

  const handleSelectAll = () => {
    if (disabled) return;
    onChange(availableTypes);
  };

  const handleClearAll = () => {
    if (disabled) return;
    onChange([]);
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Object Types</label>
        <div className="text-sm text-gray-500 italic">Loading object types...</div>
      </div>
    );
  }

  if (availableTypes.length === 0) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Object Types</label>
        <div className="text-sm text-gray-500 italic">No object types available</div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Object Types
          {value.length > 0 && (
            <span className="ml-2 text-xs text-gray-500">({value.length} selected)</span>
          )}
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSelectAll}
            disabled={disabled || value.length === availableTypes.length}
            className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            Select All
          </button>
          <button
            type="button"
            onClick={handleClearAll}
            disabled={disabled || value.length === 0}
            className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="space-y-1 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-2 bg-white">
        {availableTypes.map((type) => (
          <label
            key={type}
            className={`flex items-center px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer ${
              disabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <input
              type="checkbox"
              checked={value.includes(type)}
              onChange={() => handleToggle(type)}
              disabled={disabled}
              className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-900">{type}</span>
          </label>
        ))}
      </div>

      {value.length === 0 && (
        <div className="text-xs text-gray-500 italic">
          No object types selected (all types will be included)
        </div>
      )}
    </div>
  );
};

export default ObjectTypeFilterControl;
